import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { SelectOption } from "@/components/shared/select/Select";
import { FormField, FormSelectField } from "@/v2/components/form/form-schema";
import filterMenuStyles from "@/v2/components/filter-menu/FilterMenu.module.scss";
import { Cross1Icon } from "@radix-ui/react-icons";
import { Flex, IconButton, Text } from "@radix-ui/themes";
import { useCallback, useMemo } from "react";
import { FilterRuleConditionValueTextOrKnown } from "./FilterRuleConditionValueTextOrKnown";
import {
  FilterOperator,
  isMessageFilterKnownValue,
  MessageFilter,
  MESSAGE_FILTER_KNOWN_VALUE,
  operatorOptions,
  isValuelessOperator,
} from "./SystemMessage.model";

export const FILTER_RULE_PILL_CLASS =
  "rt-reset rt-BaseButton rt-r-size-2 rt-variant-soft rt-high-contrast rt-Button";

function getOperatorsForField(
  field: FormField | undefined
): SelectOption[] {
  if (!field) return operatorOptions;
  if (field.kind === "boolean") {
    return operatorOptions.filter(
      (o) => o.value === "eq" || o.value === "ne"
    );
  }
  if (field.kind === "select") {
    return operatorOptions.filter(
      (o) =>
        o.value === "eq" ||
        o.value === "ne" ||
        o.value === "like" ||
        o.value === "exists" ||
        o.value === "notExists"
    );
  }
  if (field.kind === "number") {
    return operatorOptions.filter(
      (o) =>
        o.value === "eq" ||
        o.value === "gt" ||
        o.value === "gte" ||
        o.value === "lt" ||
        o.value === "lte"
    );
  }
  return operatorOptions.filter(
    (o) => o.value !== "gt" && o.value !== "gte" && o.value !== "lt" && o.value !== "lte"
  );
}

function isSelectFieldWithOptions(
  field: FormField | undefined
): field is FormSelectField {
  return (
    field?.kind === "select" &&
    "options" in field &&
    field.options.length > 0
  );
}

type FilterValueUpdate = Partial<
  Pick<MessageFilter, "value" | "knownSourceFieldId">
>;

function ValueEditor({
  filter,
  fieldConfig,
  contextFields,
  onValueUpdate,
}: {
  filter: MessageFilter;
  fieldConfig: FormField | undefined;
  contextFields: FormField[];
  onValueUpdate: (updates: FilterValueUpdate) => void;
}) {
  if (isValuelessOperator(filter.operator)) return null;

  if (fieldConfig?.kind === "boolean") {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <div
            data-accent-color="gray"
            className={`${FILTER_RULE_PILL_CLASS} shrink`}
          >
            {filter.value === "true"
              ? "True"
              : filter.value === "false"
                ? "False"
                : "Select..."}
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuCheckboxItem
            checked={filter.value === "true"}
            onCheckedChange={() =>
              onValueUpdate({ value: "true", knownSourceFieldId: undefined })
            }
          >
            True
          </DropdownMenuCheckboxItem>
          <DropdownMenuCheckboxItem
            checked={filter.value === "false"}
            onCheckedChange={() =>
              onValueUpdate({ value: "false", knownSourceFieldId: undefined })
            }
          >
            False
          </DropdownMenuCheckboxItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  if (isSelectFieldWithOptions(fieldConfig) && filter.operator !== "like") {
    const selectedOption = fieldConfig.options.find(
      (o) => String(o.value) === filter.value
    );
    const triggerLabel = isMessageFilterKnownValue(filter.value)
      ? MESSAGE_FILTER_KNOWN_VALUE
      : selectedOption?.label ?? (filter.value || "Select…");

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <div
            data-accent-color="gray"
            className={`${FILTER_RULE_PILL_CLASS} shrink`}
          >
            <Text truncate>{triggerLabel}</Text>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {fieldConfig.options.map((opt) => (
            <DropdownMenuCheckboxItem
              key={String(opt.value)}
              checked={
                !isMessageFilterKnownValue(filter.value) &&
                filter.value === String(opt.value)
              }
              onCheckedChange={(checked) => {
                if (!checked) return;
                onValueUpdate({
                  value: String(opt.value),
                  knownSourceFieldId: undefined,
                });
              }}
            >
              {opt.label}
            </DropdownMenuCheckboxItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuCheckboxItem
            checked={isMessageFilterKnownValue(filter.value)}
            onCheckedChange={(checked) => {
              if (!checked) return;
              onValueUpdate({
                value: MESSAGE_FILTER_KNOWN_VALUE,
                knownSourceFieldId: undefined,
              });
            }}
          >
            {MESSAGE_FILTER_KNOWN_VALUE}
          </DropdownMenuCheckboxItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <FilterRuleConditionValueTextOrKnown
      filter={filter}
      fieldLabel={fieldConfig?.label ?? "Value"}
      contextFields={contextFields}
      onValueUpdate={onValueUpdate}
    />
  );
}

export function FilterRuleConditionRow({
  filter,
  fieldConfig,
  contextFields,
  onUpdate,
  onRemove,
}: {
  filter: MessageFilter;
  fieldConfig: FormField | undefined;
  contextFields: FormField[];
  onUpdate: (updates: Partial<MessageFilter>) => void;
  onRemove: () => void;
}) {
  const operators = useMemo(
    () => getOperatorsForField(fieldConfig),
    [fieldConfig]
  );

  const operatorLabel = useMemo(
    () =>
      operators.find((o) => o.value === filter.operator)?.label ??
      filter.operator,
    [operators, filter.operator]
  );

  const handleOperatorChange = useCallback(
    (operator: string) => {
      const newOp = operator as FilterOperator;
      if (isValuelessOperator(newOp)) {
        onUpdate({
          operator: newOp,
          value: "",
          knownSourceFieldId: undefined,
        });
      } else {
        onUpdate({ operator: newOp });
      }
    },
    [onUpdate]
  );

  const handleValueUpdate = useCallback(
    (updates: FilterValueUpdate) => onUpdate(updates),
    [onUpdate]
  );

  return (
    <Flex gap="1px" className={filterMenuStyles.activeFilter} align="center">
      <div
        data-accent-color="gray"
        className={`${FILTER_RULE_PILL_CLASS} pointer-events-none`}
      >
        {fieldConfig?.label ?? filter.field}
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <div data-accent-color="gray" className={FILTER_RULE_PILL_CLASS}>
            {operatorLabel}
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {operators.map((op) => (
            <DropdownMenuCheckboxItem
              key={op.value}
              checked={filter.operator === op.value}
              onCheckedChange={() => handleOperatorChange(op.value)}
            >
              {op.label}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <ValueEditor
        filter={filter}
        fieldConfig={fieldConfig}
        contextFields={contextFields}
        onValueUpdate={handleValueUpdate}
      />

      <IconButton variant="soft" color="gray" size="2" onClick={onRemove}>
        <Cross1Icon />
      </IconButton>
    </Flex>
  );
}
